import { type EvidenceTypeEnum, type EvidenceUploadMode } from 'api';
import { translate } from 'i18n';
import { addError, logger } from 'monitor';
import type { ChangeEvent } from 'react';
import { FilePicker } from 'ui';
import { useNotification } from '../../../notifications/hooks/useNotification/useNotification';

import { ALLOWED_EVIDENCE_UPLOAD_FILE_TYPES } from '../../config';
import { useOfflineEvidence } from '../../hooks/useOfflineEvidence/useOfflineEvidence';

type Props = {
  mode: EvidenceUploadMode;
  evidenceType: EvidenceTypeEnum;
  parentId: string;
  onSuccess?: () => void;
  label?: string;
  metadata?: Record<string, unknown>;
  model?: string;
};

export const EvidenceUploadButton = ({
  onSuccess,
  mode,
  parentId,
  evidenceType,
  label = translate('backlog.evidenceUploadButton.label', { namespace: 'features' }),
  metadata,
  model,
}: Props) => {
  const { open } = useNotification();
  const { mutateAsync: uploadEvidence, isPending } = useOfflineEvidence({
    mode,
    mutationOptions: {
      onError: (error, { photo }: { photo: File }) => {
        open({
          message: translate('backlog.evidenceUploadButton.notifications.failedToUploadPhoto', {
            namespace: 'features',
            values: {
              error: String(error),
              photoName: photo.name,
            },
          }),
          type: 'error',
        });
      },
      onSuccess: () => {
        onSuccess?.();
      },
    },
    parentId,
  });

  const handleUploadEvidence = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event?.target?.files;
    if (!files?.length) {
      addError(Error('No file selected'));
      return;
    }

    try {
      const uploadPromises = [];
      for (let i = 0; i < files.length; i++) {
        uploadPromises.push(
          uploadEvidence({
            evidenceType,
            metadata,
            model,
            parentId,
            photo: files[i],
            startTime: Date.now(),
          })
        );
      }

      await Promise.all(uploadPromises);

      open({
        message: translate('backlog.evidenceUploadButton.notifications.successfullyUploadedEvidence', {
          namespace: 'features',
        }),
        type: 'success',
      });
    } catch (error) {
      logger.error('Failed to upload evidence', error, {
        evidenceType,
        fileCount: files.length,
        parentId,
      });
      open({
        message: translate('backlog.evidenceUploadButton.notifications.failedToUploadEvidence', {
          namespace: 'features',
        }),
        type: 'error',
      });
    }
  };

  return (
    <FilePicker
      multiple
      accept={ALLOWED_EVIDENCE_UPLOAD_FILE_TYPES}
      onChange={handleUploadEvidence}
      label={label}
      buttonProps={{ fullWidth: true, loading: isPending, size: 'medium' }}
    />
  );
};
